import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { RootState } from '../redux/store';
import { registerEnrollmentDates, fetchEnrollmentDates, clearAllEnrollmentMessages } from '../redux/actions/enrollmentActions';
import { useNavigate } from 'react-router-dom';
import styles from './EnrollmentDatesPortal.module.css';
import ReturnToDashboard from './ReturnToDashboard';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { FaDownload, FaExclamationCircle } from 'react-icons/fa';

const EnrollmentDatesPortal: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, Action>>();
  const navigate = useNavigate();
  const { loading, error, enrollments } = useSelector((state: RootState) => state.enrollment);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'loading' | 'no-data' | 'ready'>('idle');

  const [formData, setFormData] = useState({
    employerName: '',
    enrollmentStartDate: null as Date | null,
    enrollmentEndDate: null as Date | null,
    year: new Date().getFullYear().toString()
  });

  const [validated, setValidated] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const topRef = React.useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (showSuccessMessage || validationError) {
      scrollToTop();
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setValidationError(null);
        handleClear();
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage, validationError]);

  useEffect(() => {
    const fetchData = async () => {
      setDownloadStatus('loading');
      try {
        const result = await dispatch(fetchEnrollmentDates() as ThunkAction<Promise<any>, RootState, unknown, Action>);
        if (result.success && result.data && result.data.length > 0) {
          setDownloadStatus('ready');
        } else {
          setDownloadStatus('no-data');
        }
      } catch (error) {
        console.error('Error fetching enrollment dates:', error);
        setDownloadStatus('no-data');
      }
    };

    fetchData();
  }, [dispatch]);

  const handleDownload = async () => {
    try {
      if (!enrollments.length) {
        return;
      }

      const currentYear = new Date().getFullYear();
      const enrollmentsForYear = enrollments.filter(e => e.year === currentYear.toString());
      
      if (enrollmentsForYear.length === 0) {
        setDownloadStatus('no-data');
        return;
      }

      const headers = ['Employer Name', 'Start Date', 'End Date', 'Year', 'Process State'];
      const csvContent = [
        headers.join(','),
        ...enrollmentsForYear.map(e => [
          e.employer_name,
          e.enrollment_start_date,
          e.enrollment_end_date,
          e.year,
          e.process_state
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `enrollment_dates_${currentYear}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setDownloadStatus('no-data');
    }
  };

  const renderDownloadButton = () => {
    switch (downloadStatus) {
      case 'loading':
        return (
          <button className={styles.downloadButton} disabled>
            <span className={styles.downloadSpinner}></span>
            Loading...
          </button>
        );
      case 'no-data':
        return (
          <button className={`${styles.downloadButton} ${styles.noData}`} disabled>
            <FaExclamationCircle className={styles.downloadIcon} />
            No Data Available
          </button>
        );
      case 'ready':
        return (
          <button 
            className={styles.downloadButton}
            onClick={handleDownload}
          >
            <FaDownload className={styles.downloadIcon} />
            Download
          </button>
        );
      default:
        return (
          <button className={styles.downloadButton} disabled>
            <FaDownload className={styles.downloadIcon} />
            Download
          </button>
        );
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setValidationError(null);

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    const startDate = new Date(formData.enrollmentStartDate!);
    const endDate = new Date(formData.enrollmentEndDate!);
    const year = parseInt(formData.year);

    if (startDate > endDate) {
      setValidationError('Enrollment start date cannot be later than end date');
      return;
    }

    if (startDate.getFullYear() < year - 1 || startDate.getFullYear() > year ||
        endDate.getFullYear() < year - 1 || endDate.getFullYear() > year) {
      setValidationError(`Enrollment period must be within ${year-1} to ${year}`);
      return;
    }

    setValidated(true);

    try {
      const result = await dispatch(registerEnrollmentDates(formData) as ThunkAction<Promise<any>, RootState, unknown, Action>);
      if (result.success) {
        setShowSuccessMessage(true);
        dispatch(fetchEnrollmentDates() as ThunkAction<Promise<any>, RootState, unknown, Action>);
      } else if (result.error) {
        setValidationError(result.error);
      }
    } catch (error) {
      console.error('Error registering enrollment dates:', error);
      setValidationError('An error occurred while registering enrollment dates');
    }
  };

  const handleClear = () => {
    setFormData({
      employerName: '',
      enrollmentStartDate: null,
      enrollmentEndDate: null,
      year: new Date().getFullYear().toString()
    });
    setValidated(false);
    dispatch(clearAllEnrollmentMessages());
  };

  return (
    <div className={styles.enrollmentPortal}>
      <div ref={topRef} />
      <ReturnToDashboard />
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>Register Enrollment Dates Portal</h1>
        </header>

        <div className={styles.formContainer}>
          {showSuccessMessage && (
            <div className={styles.successMessage}>
              Enrollment dates registered successfully!
            </div>
          )}

          {validationError && (
            <div className={styles.errorMessage}>
              {validationError}
            </div>
          )}

          <div className={styles.downloadSection}>
            <div className={styles.downloadHeader}>
              <h3>Current Year Enrollment Data</h3>
              {renderDownloadButton()}
            </div>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="employerName">Employer Name <span className={styles.required}>*</span></label>
              <input
                type="text"
                id="employerName"
                value={formData.employerName}
                onChange={(e) => setFormData({...formData, employerName: e.target.value})}
                className={styles.formControl}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="enrollmentStartDate">Enrollment Start Date <span className={styles.required}>*</span></label>
              <DatePicker
                selected={formData.enrollmentStartDate}
                onChange={(date) => setFormData({...formData, enrollmentStartDate: date})}
                className={styles.datePicker}
                dateFormat="MM/dd/yyyy"
                required
                placeholderText="Select start date"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="enrollmentEndDate">Enrollment End Date <span className={styles.required}>*</span></label>
              <DatePicker
                selected={formData.enrollmentEndDate}
                onChange={(date) => setFormData({...formData, enrollmentEndDate: date})}
                className={styles.datePicker}
                dateFormat="MM/dd/yyyy"
                required
                placeholderText="Select end date"
                minDate={formData.enrollmentStartDate || undefined}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="year">Year <span className={styles.required}>*</span></label>
              <input
                type="text"
                id="year"
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: e.target.value})}
                className={styles.formControl}
                required
                pattern="\d{4}"
              />
            </div>

            <div className={styles.requiredNote}>* Required fields</div>

            <div className={styles.buttonGroup}>
              <button type="button" onClick={handleClear} className={styles.clearButton}>
                Clear
              </button>
              <button type="submit" className={styles.submitButton}>
                Submit
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
};

export default EnrollmentDatesPortal; 